import { type LatenessConfigResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { differenceInMinutes, min } from "date-fns";

import { findLatenessConfigBasedOnMinutesLate } from "../../utils/getLatenessConfig";

interface WouldAdjustedClockInChangeScoreParams {
  /** How many minutes late the worker was with the recorded arrival. */
  recordedLatenessMinutes: number;
  /** The adjusted clock-in time. */
  adjustedClockInTime: Date;
  /** The expected arrival time — targetArrivalTime for short-lead-time bookings, otherwise shift start. */
  expectedArrivalTime: Date;
  /** The geofence entry time, if available. */
  geofenceEntry?: Date;
  /** Grace period before a worker is considered late. */
  gracePeriodMinutes: number;
  /** The lateness penalty buckets from the worker's clipboard score profile. */
  latenessConfig: LatenessConfigResponse;
}

/**
 * Determines whether using the adjusted clock-in time instead of the recorded
 * clock-in time would result in a different clipboard score adjustment.
 *
 * Returns `true` when:
 * - The recorded clock-in time was late but the adjusted clock-in time makes the worker on-time (within grace period), or
 * - The adjusted clock-in time falls into a different lateness penalty bucket.
 */
export function wouldAdjustedClockInChangeScore(
  params: WouldAdjustedClockInChangeScoreParams
): boolean {
  const {
    recordedLatenessMinutes,
    adjustedClockInTime,
    expectedArrivalTime,
    geofenceEntry,
    gracePeriodMinutes,
    latenessConfig,
  } = params;

  const adjustedArrival = geofenceEntry
    ? min([adjustedClockInTime, geofenceEntry])
    : adjustedClockInTime;
  const adjustedLatenessMinutes = differenceInMinutes(adjustedArrival, expectedArrivalTime);

  const recordedIsOnTime = recordedLatenessMinutes <= gracePeriodMinutes;
  const adjustedIsOnTime = adjustedLatenessMinutes <= gracePeriodMinutes;

  if (recordedIsOnTime && adjustedIsOnTime) {
    return false;
  }

  if (!recordedIsOnTime && adjustedIsOnTime) {
    return true;
  }

  // Both are late. If there's no lateness config, both lose the on-time bonus equally.
  if (latenessConfig.length === 0) {
    return false;
  }

  const recordedConfig = findLatenessConfigBasedOnMinutesLate(
    recordedLatenessMinutes,
    latenessConfig
  );
  const adjustedConfig = findLatenessConfigBasedOnMinutesLate(
    adjustedLatenessMinutes,
    latenessConfig
  );

  return recordedConfig?.points !== adjustedConfig?.points;
}
